import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import api from '../utils/api';
import { toast } from 'sonner';
import {
  Home, Users, FileText, BarChart3, Activity, Settings,
  Plus, Edit2, Trash2, Eye, EyeOff, Save, X, ChevronDown,
  ChevronUp, TrendingUp, DollarSign, Bell, ArrowLeft,
  Calendar, Clock, Loader, Check, AlertCircle
} from 'lucide-react';

// Configure marked
marked.setOptions({
  breaks: true,
  gfm: true
});

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL;

const CATEGORIES = ['Education', 'News', 'Tips', 'Analysis', 'Guide', 'Rate Updates', 'Tips & Guides'];

const emptyPost = {
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  category: 'Education',
  image_url: '',
  featured: false,
  published: false,
  read_time: '5 min read'
};

const AdminDashboard = ({ user }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [posts, setPosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  
  // Blog post form
  const [showPostForm, setShowPostForm] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [postForm, setPostForm] = useState(emptyPost);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  
  // Parse markdown content for preview
  const previewContent = useMemo(() => {
    if (!postForm.content) return '';
    const isHtml = /<[a-z][\s\S]*>/i.test(postForm.content);
    return isHtml ? postForm.content : marked.parse(postForm.content);
  }, [postForm.content]);
  
  useEffect(() => {
    // Check if user is admin
    if (user && ADMIN_EMAIL && user.email !== ADMIN_EMAIL) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    loadData();
  }, [user]);
  
  const loadData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, postsRes, usersRes, activityRes] = await Promise.all([
        api.get('/api/admin/analytics'),
        api.get('/api/blog/posts'),
        api.get('/api/admin/users'),
        api.get('/api/admin/activity')
      ]);
      
      setAnalytics(analyticsRes.data);
      setPosts(postsRes.data || []);
      setUsers(usersRes.data?.users || []);
      setActivity(activityRes.data || []);
    } catch (error) {
      console.error('Error loading admin data:', error);
      if (error.response?.status === 403) {
        setAccessDenied(true);
      } else {
        toast.error('Failed to load admin data');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handlePostChange = (e) => {
    const { name, value, type, checked } = e.target;
    setPostForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };
  
  const handleTitleChange = (e) => {
    const title = e.target.value;
    setPostForm(prev => ({
      ...prev,
      title,
      slug: editingPost ? prev.slug : generateSlug(title)
    }));
  };
  
  const handlePostSubmit = async (e) => {
    e.preventDefault();
    
    if (!postForm.title || !postForm.slug || !postForm.excerpt) {
      toast.error('Please fill in title, slug, and excerpt');
      return;
    }
    
    setSaving(true);
    try {
      if (editingPost) {
        await api.put(
          `/api/admin/blog/posts/${editingPost.post_id}`,
          postForm
        );
        toast.success('Post updated successfully');
      } else {
        await api.post('/api/admin/blog/posts', postForm);
        toast.success('Post created successfully');
      }
      
      setShowPostForm(false);
      setEditingPost(null);
      setPostForm(emptyPost);
      loadData();
    } catch (error) {
      console.error('Error saving post:', error);
      toast.error(error.response?.data?.detail || 'Failed to save post');
    } finally {
      setSaving(false);
    }
  };
  
  const handleEditPost = (post) => {
    setPostForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content || '',
      category: post.category,
      image_url: post.image_url || '',
      featured: post.featured,
      published: post.published,
      read_time: post.read_time || '5 min read'
    });
    setEditingPost(post);
    setShowPostForm(true);
  };
  
  const handleDeletePost = async (postId) => {
    try {
      await api.delete(`/api/admin/blog/posts/${postId}`);
      toast.success('Post deleted');
      setDeleteConfirm(null);
      loadData();
    } catch (error) {
      console.error('Error deleting post:', error);
      toast.error('Failed to delete post');
    }
  };
  
  const handleCancelForm = () => {
    setShowPostForm(false);
    setEditingPost(null);
    setPostForm(emptyPost);
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-CA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  const getActivityIcon = (type) => {
    switch (type) {
      case 'signup': return <Users className="h-4 w-4 text-blue-400" />;
      case 'blog': return <FileText className="h-4 w-4 text-green-400" />;
      case 'offer': return <DollarSign className="h-4 w-4 text-yellow-400" />;
      default: return <Activity className="h-4 w-4 text-muted-foreground" />;
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (accessDenied) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center p-8 bg-card border border-border rounded-xl max-w-md">
          <AlertCircle className="h-16 w-16 text-red-400 mx-auto mb-4" />
          <h2 className="text-2xl font-heading font-bold mb-2">Access Denied</h2>
          <p className="text-muted-foreground mb-6">
            You don't have permission to access the admin dashboard. 
            Only the site administrator can access this page.
          </p>
          <Link 
            to="/dashboard" 
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
          >
            <ArrowLeft className="h-4 w-4" />
            Go to Dashboard
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2">
                <Home className="h-6 w-6 text-primary" />
                <span className="text-xl font-heading font-bold">Admin Dashboard</span>
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <span className="text-sm text-muted-foreground">{user?.email}</span>
              <Link
                to="/dashboard"
                className="flex items-center gap-2 px-3 py-1.5 bg-muted hover:bg-muted/80 rounded-lg text-sm transition-colors"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to App
              </Link>
            </div>
          </div>
        </div>
      </header>
      
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-8 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'blog', label: 'Blog Posts', icon: FileText },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'activity', label: 'Activity', icon: Activity }
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                  activeTab === tab.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-card border border-border hover:bg-muted'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
        
        {/* Overview Tab */}
        {activeTab === 'overview' && analytics && (
          <div className="space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-blue-500/10 rounded-lg">
                    <Users className="h-5 w-5 text-blue-400" />
                  </div>
                  <span className="text-sm text-muted-foreground">Total Users</span>
                </div>
                <p className="text-3xl font-bold">{analytics.users.total}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  +{analytics.users.recent_signups} this month
                </p>
              </div>
              
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-green-500/10 rounded-lg">
                    <DollarSign className="h-5 w-5 text-green-400" />
                  </div>
                  <span className="text-sm text-muted-foreground">Paid Users</span>
                </div>
                <p className="text-3xl font-bold">{analytics.users.premium + analytics.users.pro}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {analytics.users.premium} Premium, {analytics.users.pro} Pro
                </p>
              </div>
              
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-purple-500/10 rounded-lg">
                    <FileText className="h-5 w-5 text-purple-400" />
                  </div>
                  <span className="text-sm text-muted-foreground">Blog Posts</span>
                </div>
                <p className="text-3xl font-bold">{analytics.blog.total_posts}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {analytics.blog.published_posts} published
                </p>
              </div>
              
              <div className="bg-card border border-border rounded-xl p-5">
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-orange-500/10 rounded-lg">
                    <Eye className="h-5 w-5 text-orange-400" />
                  </div>
                  <span className="text-sm text-muted-foreground">Blog Views</span>
                </div>
                <p className="text-3xl font-bold">{analytics.blog.total_views}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  All time views
                </p>
              </div>
            </div>
            
            {/* Engagement Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-card border border-border rounded-xl p-5">
                <h3 className="font-semibold mb-4">User Engagement</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Mortgages Tracked</span>
                    <span className="font-mono font-medium">{analytics.engagement.mortgages_tracked}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Offers Added</span>
                    <span className="font-mono font-medium">{analytics.engagement.offers_added}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Rate Alerts</span>
                    <span className="font-mono font-medium">{analytics.engagement.rate_alerts}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Active Alerts</span>
                    <span className="font-mono font-medium text-primary">{analytics.engagement.active_alerts}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-card border border-border rounded-xl p-5">
                <h3 className="font-semibold mb-4">Subscription Breakdown</h3>
                <div className="space-y-3">
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Free</span>
                      <span>{analytics.users.free}</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-muted-foreground h-2 rounded-full"
                        style={{ width: `${(analytics.users.free / analytics.users.total) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Premium</span>
                      <span>{analytics.users.premium}</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-primary h-2 rounded-full"
                        style={{ width: `${(analytics.users.premium / analytics.users.total) * 100}%` }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between text-sm mb-1">
                      <span>Pro</span>
                      <span>{analytics.users.pro}</span>
                    </div>
                    <div className="w-full bg-muted rounded-full h-2">
                      <div 
                        className="bg-purple-500 h-2 rounded-full"
                        style={{ width: `${(analytics.users.pro / analytics.users.total) * 100}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-card border border-border rounded-xl p-5">
                <h3 className="font-semibold mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  {activity.slice(0, 5).map((item, idx) => (
                    <div key={`${item.type}-${item.timestamp || idx}`} className="flex items-start gap-2">
                      {getActivityIcon(item.type)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm truncate">{item.description}</p>
                        <p className="text-xs text-muted-foreground">{formatDate(item.timestamp)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Blog Posts Tab */}
        {activeTab === 'blog' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Blog Posts</h2>
              {!showPostForm && (
                <button
                  onClick={() => setShowPostForm(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                >
                  <Plus className="h-4 w-4" />
                  New Post
                </button>
              )}
            </div>
            
            {/* Post Form */}
            {showPostForm && (
              <div className="bg-card border border-primary/30 rounded-xl p-6">
                <h3 className="text-lg font-semibold mb-4">
                  {editingPost ? 'Edit Post' : 'Create New Post'}
                </h3>
                <form onSubmit={handlePostSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Title *</label>
                      <input
                        type="text"
                        name="title"
                        value={postForm.title}
                        onChange={handleTitleChange}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
                        placeholder="Post title"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Slug *</label>
                      <input
                        type="text"
                        name="slug"
                        value={postForm.slug}
                        onChange={handlePostChange}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
                        placeholder="post-url-slug"
                        required
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium mb-1.5">Excerpt *</label>
                    <textarea
                      name="excerpt"
                      value={postForm.excerpt}
                      onChange={handlePostChange}
                      rows={2}
                      className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary resize-none"
                      placeholder="Brief description of the post"
                      required
                    />
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-sm font-medium">Content</label>
                      <div className="flex gap-1 bg-muted rounded-lg p-0.5">
                        <button
                          type="button"
                          onClick={() => setShowPreview(false)}
                          className={`px-3 py-1 text-xs rounded-md transition-colors ${!showPreview ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}
                        >
                          Write
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowPreview(true)}
                          className={`px-3 py-1 text-xs rounded-md transition-colors ${showPreview ? 'bg-background shadow-sm' : 'hover:bg-background/50'}`}
                        >
                          Preview
                        </button>
                      </div>
                    </div>
                    
                    {!showPreview ? (
                      <textarea
                        name="content"
                        value={postForm.content}
                        onChange={handlePostChange}
                        rows={12}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary resize-none font-mono text-sm"
                        placeholder="Write your content using Markdown:

# Heading 1
## Heading 2
### Heading 3

**bold text** or *italic text*

- Bullet point 1
- Bullet point 2

1. Numbered item
2. Another item

[Link text](https://example.com)

> Blockquote text"
                      />
                    ) : (
                      <div 
                        className="w-full min-h-[300px] px-4 py-3 bg-background border border-border rounded-lg overflow-auto blog-content text-sm"
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewContent || '<p class="text-muted-foreground italic">Nothing to preview yet...</p>') }}
                      />
                    )}
                    <p className="text-xs text-muted-foreground mt-1">
                      Supports Markdown formatting. Use **bold**, *italic*, # headings, - lists, and [links](url).
                    </p>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Category</label>
                      <select
                        name="category"
                        value={postForm.category}
                        onChange={handlePostChange}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
                      >
                        {CATEGORIES.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Read Time</label>
                      <input
                        type="text"
                        name="read_time"
                        value={postForm.read_time}
                        onChange={handlePostChange}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
                        placeholder="5 min read"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1.5">Image URL</label>
                      <input
                        type="url"
                        name="image_url"
                        value={postForm.image_url}
                        onChange={handlePostChange}
                        className="w-full px-3 py-2 bg-background border border-border rounded-lg focus:ring-2 focus:ring-primary"
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="featured"
                        checked={postForm.featured}
                        onChange={handlePostChange}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                      />
                      <span className="text-sm">Featured Post</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        name="published"
                        checked={postForm.published}
                        onChange={handlePostChange}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-primary"
                      />
                      <span className="text-sm">Published</span>
                    </label>
                  </div>
                  
                  <div className="flex justify-end gap-3 pt-4">
                    <button
                      type="button"
                      onClick={handleCancelForm}
                      className="px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={saving}
                      className="flex items-center gap-2 px-6 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
                    >
                      {saving ? (
                        <Loader className="h-4 w-4 animate-spin" />
                      ) : (
                        <Save className="h-4 w-4" />
                      )}
                      {editingPost ? 'Update' : 'Create'} Post
                    </button>
                  </div>
                </form>
              </div>
            )}
            
            {/* Posts List */}
            <div className="space-y-3">
              {posts.length === 0 ? (
                <div className="bg-card border border-border rounded-xl p-12 text-center">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No Blog Posts Yet</h3>
                  <p className="text-muted-foreground mb-4">Create your first blog post to get started.</p>
                </div>
              ) : (
                posts.map(post => (
                  <div
                    key={post.post_id}
                    className="bg-card border border-border rounded-xl p-4 hover:border-primary/30 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="font-semibold truncate">{post.title}</h4>
                          {post.featured && (
                            <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 text-xs rounded-full">
                              Featured
                            </span>
                          )}
                          {post.published ? (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full flex items-center gap-1">
                              <Eye className="h-3 w-3" />
                              Published
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-muted text-muted-foreground text-xs rounded-full flex items-center gap-1">
                              <EyeOff className="h-3 w-3" />
                              Draft
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground truncate mb-2">{post.excerpt}</p>
                        <div className="flex items-center gap-4 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {formatDate(post.created_at)}
                          </span>
                          <span>{post.category}</span>
                          <span className="flex items-center gap-1">
                            <Eye className="h-3 w-3" />
                            {post.views || 0} views
                          </span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditPost(post)}
                          className="p-2 hover:bg-muted rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        {deleteConfirm === post.post_id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDeletePost(post.post_id)}
                              className="p-2 bg-red-500 text-white rounded-lg hover:bg-red-600"
                            >
                              <Check className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(null)}
                              className="p-2 hover:bg-muted rounded-lg"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteConfirm(post.post_id)}
                            className="p-2 hover:bg-red-500/10 text-red-400 rounded-lg transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
        
        {/* Users Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Users ({users.length})</h2>
            </div>
            
            {users.length === 0 ? (
              <div className="bg-card border border-border rounded-xl p-12 text-center">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No Users Yet</h3>
                <p className="text-muted-foreground">Users will appear here when they sign up.</p>
              </div>
            ) : (
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left px-4 py-3 text-sm font-medium">User</th>
                      <th className="text-left px-4 py-3 text-sm font-medium">Plan</th>
                      <th className="text-left px-4 py-3 text-sm font-medium">Joined</th>
                      <th className="text-right px-4 py-3 text-sm font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {users.map(u => (
                      <tr key={u.user_id} className="hover:bg-muted/30">
                        <td className="px-4 py-3">
                          <p className="font-medium">{u.name || 'Unknown'}</p>
                          <p className="text-sm text-muted-foreground">{u.email}</p>
                        </td>
                        <td className="px-4 py-3">
                          <select
                            value={u.subscription_tier || 'free'}
                            onChange={async (e) => {
                              try {
                                await api.put(`/api/admin/users/${u.user_id}`, {
                                  subscription_tier: e.target.value
                                });
                                toast.success(`Updated ${u.name}'s subscription to ${e.target.value}`);
                                loadData();
                              } catch (err) {
                                toast.error('Failed to update subscription');
                              }
                            }}
                            className={`px-2 py-1 rounded-lg text-xs font-medium bg-transparent border cursor-pointer ${
                              u.subscription_tier === 'pro' 
                                ? 'border-purple-500/50 text-purple-400'
                                : u.subscription_tier === 'premium'
                                ? 'border-primary/50 text-primary'
                                : 'border-border text-muted-foreground'
                            }`}
                          >
                            <option value="free">Free</option>
                            <option value="premium">Premium</option>
                            <option value="pro">Pro</option>
                          </select>
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {formatDate(u.created_at)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          {u.email !== ADMIN_EMAIL && (
                            <button
                              onClick={async () => {
                                if (window.confirm(`Are you sure you want to delete ${u.name || u.email}? This cannot be undone.`)) {
                                  try {
                                    await api.delete(`/api/admin/users/${u.user_id}`);
                                    toast.success('User deleted');
                                    loadData();
                                  } catch (err) {
                                    toast.error('Failed to delete user');
                                  }
                                }
                              }}
                              className="p-2 text-red-400 hover:bg-red-500/20 rounded-lg transition-colors"
                              title="Delete user"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
        
        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <div className="space-y-6">
            <h2 className="text-xl font-semibold">Recent Activity</h2>
            
            <div className="space-y-3">
              {activity.length === 0 ? (
                <div className="bg-card border border-border rounded-xl p-12 text-center">
                  <Activity className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold">No Activity Yet</h3>
                </div>
              ) : (
                activity.map((item, idx) => (
                  <div
                    key={`${item.type}-${item.timestamp || idx}`}
                    className="bg-card border border-border rounded-xl p-4 flex items-center gap-4"
                  >
                    <div className="p-2 bg-muted rounded-lg">
                      {getActivityIcon(item.type)}
                    </div>
                    <div className="flex-1">
                      <p className="font-medium">{item.description}</p>
                      <p className="text-sm text-muted-foreground">{formatDate(item.timestamp)}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
